/**
 * SearchResultsPage.tsx
 * Search results page with filters and faceted navigation
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useMarketplaceSearch, useCategories } from '../hooks/useMarketplace';
import { ProductCard, VendorCard, MarketplaceFilters } from '../components';
import { MarketplaceFilters as FilterType } from '../types/marketplaceTypes';

type ResultType = 'all' | 'products' | 'vendors';

export const SearchResultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [resultType, setResultType] = useState<ResultType>('all');
  const [filters, setFilters] = useState<FilterType>({});

  const { data: searchResults, isLoading, error } = useMarketplaceSearch(query);
  const { data: categories } = useCategories();

  const filteredResults = useMemo(() => {
    if (!searchResults) return { products: [], vendors: [] };

    let products = searchResults.products || [];
    let vendors = searchResults.vendors || [];

    // Apply category filter
    if (filters.categoryId) {
      products = products.filter(p => p.categoryId === filters.categoryId);
      vendors = vendors.filter(v => v.categoryIds?.includes(filters.categoryId!));
    }

    // Apply price filter
    if (filters.priceMin !== undefined) {
      products = products.filter(p => (p.priceMin || 0) >= filters.priceMin!);
    }
    if (filters.priceMax !== undefined) {
      products = products.filter(p => (p.priceMax || p.priceMin || 0) <= filters.priceMax!);
    }

    // Apply rating filter
    if (filters.minRating) {
      products = products.filter(p => (p.avgRating ?? 0) >= filters.minRating!);
      vendors = vendors.filter(v => (v.avgRating ?? 0) >= filters.minRating!);
    }

    // Apply insurance filter
    if (filters.acceptsInsurance) {
      products = products.filter(p => p.acceptsInsurance);
      vendors = vendors.filter(v => v.insuranceAccepted && v.insuranceAccepted.length > 0);
    }

    return { products, vendors };
  }, [searchResults, filters]);

  const totalResults = filteredResults.products.length + filteredResults.vendors.length;

  const handleSearch = (newQuery: string) => {
    setSearchParams({ q: newQuery });
  };

  const resultTabs: { id: ResultType; label: string; count: number }[] = [
    { id: 'all', label: 'All Results', count: totalResults },
    { id: 'products', label: 'Products', count: filteredResults.products.length },
    { id: 'vendors', label: 'Vendors', count: filteredResults.vendors.length },
  ];

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex items-center gap-2 text-sm text-[#8B8178] mb-4">
            <Link to="/marketplace" className="hover:text-[#7A9E7E]">Marketplace</Link>
            <span>/</span>
            <span className="text-[#2B3A4D]">Search Results</span>
          </nav>

          <h1 className="text-3xl font-semibold text-[#2B3A4D] font-['Fraunces'] mb-2">
            Search Results
          </h1>
          {query && (
            <p className="text-[#8B8178]">
              {totalResults} results for "{query}"
            </p>
          )}
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-8">
          <div className="relative">
            <input
              type="text"
              defaultValue={query}
              placeholder="Search products and vendors..."
              className="w-full pl-12 pr-4 py-3 border border-[#E5E0DB] rounded-lg focus:outline-none focus:border-[#7A9E7E] focus:ring-2 focus:ring-[#7A9E7E]/20"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch((e.target as HTMLInputElement).value);
                }
              }}
            />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B8178]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <aside className="w-64 flex-shrink-0 hidden lg:block">
            <MarketplaceFilters
              categories={categories || []}
              filters={filters}
              onFiltersChange={setFilters}
              orientation="vertical"
            />
          </aside>

          {/* Results */}
          <main className="flex-1">
            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-[#E5E0DB]">
              {resultTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setResultType(tab.id)}
                  className={`pb-3 px-1 font-medium transition-colors relative ${
                    resultType === tab.id
                      ? 'text-[#7A9E7E]'
                      : 'text-[#8B8178] hover:text-[#2B3A4D]'
                  }`}
                >
                  {tab.label}
                  <span className="ml-2 text-sm">({tab.count})</span>
                  {resultType === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A9E7E]" />
                  )}
                </button>
              ))}
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="aspect-square bg-[#F5EDE4] rounded-lg mb-4" />
                    <div className="h-4 bg-[#F5EDE4] rounded w-3/4 mb-2" />
                    <div className="h-4 bg-[#F5EDE4] rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <p className="text-[#C4785A] mb-4">Error loading search results</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-[#7A9E7E] font-medium hover:text-[#5C7D60]"
                >
                  Try Again
                </button>
              </div>
            ) : totalResults === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-[#E5E0DB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-[#2B3A4D] mb-2">No results found</h3>
                <p className="text-[#8B8178] mb-6">
                  We couldn't find anything matching "{query}"
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-[#8B8178]">Suggestions:</p>
                  <ul className="text-sm text-[#8B8178] list-disc list-inside">
                    <li>Check your spelling</li>
                    <li>Try more general keywords</li>
                    <li>Try different keywords</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Products */}
                {(resultType === 'all' || resultType === 'products') && filteredResults.products.length > 0 && (
                  <section>
                    {resultType === 'all' && (
                      <h2 className="text-xl font-semibold text-[#2B3A4D] font-['Fraunces'] mb-4">
                        Products ({filteredResults.products.length})
                      </h2>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredResults.products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  </section>
                )}

                {/* Vendors */}
                {(resultType === 'all' || resultType === 'vendors') && filteredResults.vendors.length > 0 && (
                  <section>
                    {resultType === 'all' && (
                      <h2 className="text-xl font-semibold text-[#2B3A4D] font-['Fraunces'] mb-4">
                        Vendors ({filteredResults.vendors.length})
                      </h2>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {filteredResults.vendors.map((vendor) => (
                        <VendorCard key={vendor.id} vendor={vendor} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;


